/**
 * Landing page. Deliberately light: it loads no models and no workers.
 * Dropped files are handed to the workspace through navigation state.
 */
import { Link, useNavigate } from 'react-router-dom';
import { BeforeAfter } from '../../components/BeforeAfter';
import { DropZone } from '../../components/DropZone';
import { BRAND } from '../brand';

const FEATURES = [
  { title: '100% free, no sign-up', body: 'No account, no credits, no watermark. Restore as many photos as you like.' },
  { title: 'Photos never leave your device', body: 'The AI runs inside your browser. Nothing is uploaded, ever.' },
  { title: 'Built for whole albums', body: 'Drop a folder of scans and let it work through them. Close the tab and it picks up where it left off.' },
  { title: 'Made for film and slides', body: 'Tuned for prints, 35mm and medium-format scans and slides, not just phone selfies.' },
];

const STEPS = [
  { n: '1', title: 'Drop your photos', body: 'Prints, slides or film scans. JPEG, PNG, WebP or TIFF, one at a time or a whole folder.' },
  { n: '2', title: 'Choose how much to restore', body: 'Clean grain and noise, enlarge 2x or 4x. Every AI step is optional and adjustable.' },
  { n: '3', title: 'Compare and download', body: 'Drag the slider to see before and after, then download full-size files or a ZIP.' },
];

export function Landing() {
  const navigate = useNavigate();
  return (
    <div className="mx-auto max-w-5xl px-4 pb-16 sm:px-6">
      <section className="py-14 text-center sm:py-20">
        <h1 className="mx-auto max-w-3xl font-serif text-4xl leading-tight font-semibold sm:text-5xl">
          Bring your old photos, slides and film back to life
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-ink-2">Free, private, and right in your browser.</p>
        <DropZone onFiles={(files) => navigate('/app', { state: { files } })} className="mx-auto mt-10 max-w-2xl bg-card">
          <p className="font-serif text-xl">Drop photos to start</p>
        </DropZone>
        <p className="mt-3 text-sm text-ink-3">
          Or <Link to="/app" className="text-accent underline">open the workspace</Link>.
        </p>
      </section>

      <section className="mb-16 grid items-center gap-8 md:grid-cols-2" aria-label="Example">
        <BeforeAfter before="/demo/migrant-mother-before.jpg" after="/demo/migrant-mother-after.jpg" width={788} height={1024} alt="Migrant Mother by Dorothea Lange, 1936" />
        <div>
          <h2 className="font-serif text-3xl font-semibold">Drag to compare</h2>
          <p className="mt-3 text-ink-2">
            Dust and specks removed, grain calmed and the face gently restored, done entirely in a browser with the default,
            faithful settings. Nothing invented, nothing uploaded.
          </p>
          <p className="mt-4 text-xs text-ink-3">
            Photo: Dorothea Lange, <cite>Migrant Mother</cite>, 1936. Library of Congress, Prints and Photographs Division,
            FSA/OWI Collection. Public domain.
          </p>
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Features">
        {FEATURES.map((f) => (
          <div key={f.title} className="rounded-xl border border-line bg-card p-5">
            <h2 className="font-serif text-lg font-semibold">{f.title}</h2>
            <p className="mt-2 text-sm text-ink-2">{f.body}</p>
          </div>
        ))}
      </section>

      <section className="mt-16">
        <h2 className="text-center font-serif text-3xl font-semibold">How it works</h2>
        <ol className="mt-8 grid gap-6 sm:grid-cols-3">
          {STEPS.map((s) => (
            <li key={s.n} className="text-center">
              <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-accent font-serif text-lg text-accent-ink">{s.n}</span>
              <h3 className="mt-3 font-semibold">{s.title}</h3>
              <p className="mt-1 text-sm text-ink-2">{s.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-16 rounded-2xl bg-paper-2 p-6 sm:p-8">
        <h2 className="font-serif text-2xl font-semibold">Your photos stay yours</h2>
        <p className="mt-3 max-w-3xl text-ink-2">
          {BRAND} downloads its AI models once, then does all the work on your own computer or phone. Your photos, the
          restored results and your settings are stored only in this browser. There are no accounts and no tracking
          cookies. You can delete everything from Settings at any time.
        </p>
      </section>

      <footer className="mt-16 flex flex-wrap gap-4 border-t border-line pt-6 text-sm text-ink-3">
        <Link to="/help" className="underline">Help and FAQ</Link>
        <Link to="/privacy" className="underline">Privacy</Link>
        <Link to="/about" className="underline">Credits and licences</Link>
      </footer>
    </div>
  );
}
